import React, { useEffect, useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { Loader2, AlertTriangle, FileSpreadsheet } from 'lucide-react';
import { expensasApi } from '../../lib/api';
import { formatDate } from '../../lib/utils';

const fmtMoney = (v) => {
  if (v == null || isNaN(v)) return '—';
  const n = Math.round(Number(v));
  const sign = n < 0 ? '-' : '';
  return `${sign}$${Math.abs(n).toLocaleString('es-AR', { maximumFractionDigits: 0 })}`;
};

export const DetalleSubrubroDialog = ({
  isOpen,
  onClose,
  edificioId,
  periodoId,
  rubro,
  subrubro,
  mesLabel,
  periodo, // YYYY-MM del mes clickeado (opcional, default = periodoId actual)
  importeResumen, // lo que se muestra en la celda del Resumen (monto_expensas)
}) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!isOpen || !rubro || !subrubro) return;
    let cancel = false;
    setLoading(true);
    setData(null);
    expensasApi
      .getDetalleSubrubro(edificioId, periodoId, rubro, subrubro, periodo)
      .then((r) => { if (!cancel) setData(r.data); })
      .catch(() => { if (!cancel) setData({ movimientos: [], total_mayor: 0, monto_expensas_resumen: 0, codigos: [] }); })
      .finally(() => { if (!cancel) setLoading(false); });
    return () => { cancel = true; };
  }, [isOpen, edificioId, periodoId, rubro, subrubro, periodo]);

  const totalMayor = data?.total_mayor ?? 0;
  // Comparamos contra el importe que muestra el resumen (que es $ Expensas = -importe * part).
  // Por eso usamos abs() del total del Mayor para la comparación visual.
  const totalAbs = Math.abs(totalMayor);
  const resumenAbs = Math.abs(Number(importeResumen ?? data?.monto_expensas_resumen ?? 0));
  const diff = Number((totalAbs - resumenAbs).toFixed(2));
  const isRounding = Math.abs(diff) > 0 && Math.abs(diff) < 1;
  const isOk = Math.abs(diff) < 0.01;

  return (
    <Dialog open={isOpen} onOpenChange={(o) => { if (!o) onClose && onClose(); }}>
      <DialogContent className="sm:max-w-5xl max-h-[80vh] overflow-hidden flex flex-col" data-testid="detalle-subrubro-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FileSpreadsheet className="w-5 h-5 text-indigo-600" />
            Detalle de subrubro — <span className="text-indigo-700">{subrubro}</span>
          </DialogTitle>
          <p className="text-xs text-slate-500 mt-1">
            Rubro: <b>{rubro}</b> · Período: <b>{mesLabel || '—'}</b>
            {data?.codigos?.length > 0 && (
              <> · Cuentas: {data.codigos.join(', ')}</>
            )}
          </p>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto -mx-6 px-6">
          {loading ? (
            <div className="py-16 text-center text-slate-400">
              <Loader2 className="w-6 h-6 animate-spin mx-auto" />
            </div>
          ) : !data || data.movimientos.length === 0 ? (
            <div className="py-12 text-center text-slate-500 text-sm">
              No hay movimientos para este subrubro en el período.
            </div>
          ) : (
            <table className="w-full text-xs">
              <thead className="bg-slate-50 border-b border-slate-200 text-[10px] uppercase text-slate-500 sticky top-0">
                <tr>
                  <th className="py-1.5 px-2 text-left font-medium">Código</th>
                  <th className="py-1.5 px-2 text-left font-medium">Cuenta</th>
                  <th className="py-1.5 px-2 text-left font-medium">Fecha</th>
                  <th className="py-1.5 px-2 text-left font-medium">Nro. Comp</th>
                  <th className="py-1.5 px-2 text-left font-medium">Subcuenta</th>
                  <th className="py-1.5 px-2 text-left font-medium">Detalle</th>
                  <th className="py-1.5 px-2 text-right font-medium">Importe</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {data.movimientos.map((m, i) => (
                  <tr key={i} className="hover:bg-slate-50/60">
                    <td className="py-1 px-2 font-mono text-slate-500" title={m.codigo}>{m.codigo}</td>
                    <td className="py-1 px-2 text-slate-700">{m.cuenta || '—'}</td>
                    <td className="py-1 px-2 text-slate-500 whitespace-nowrap">{m.fecha ? formatDate(m.fecha) : '—'}</td>
                    <td className="py-1 px-2 text-slate-500 whitespace-nowrap" title={m.nro_comp}>{m.nro_comp || '—'}</td>
                    <td className="py-1 px-2 text-slate-600" title={m.subcuenta}>{m.subcuenta || '—'}</td>
                    <td className="py-1 px-2 text-slate-700" title={m.detalle}>{m.detalle || '—'}</td>
                    <td className="py-1 px-2 text-right font-mono whitespace-nowrap font-medium">{fmtMoney(m.importe)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {data && data.movimientos.length > 0 && (
          <div className="border-t border-slate-200 -mx-6 px-6 pt-3 mt-2 space-y-2">
            <div className="flex justify-between items-center text-sm">
              <span className="font-medium text-slate-600">
                Total subrubro ({data.movimientos.length} movimiento{data.movimientos.length !== 1 ? 's' : ''})
              </span>
              <span className="font-mono font-semibold text-slate-900" data-testid="detalle-total-mayor">
                {fmtMoney(totalMayor)}
              </span>
            </div>
            <div className="flex justify-between items-center text-xs text-slate-500">
              <span>Importe del Resumen (columna $ Expensas)</span>
              <span className="font-mono">{fmtMoney(importeResumen ?? data.monto_expensas_resumen)}</span>
            </div>
            {!isOk && (
              <div
                className={`flex items-start gap-2 text-xs px-3 py-2 rounded-md border ${
                  isRounding
                    ? 'bg-amber-50 border-amber-200 text-amber-800'
                    : 'bg-red-50 border-red-300 text-red-800'
                }`}
                data-testid="detalle-warning"
              >
                <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
                <div>
                  {isRounding ? (
                    <>Diferencia por redondeo: <b>{fmtMoney(Math.abs(diff))}</b></>
                  ) : (
                    <>Revisar: el total del detalle (<b>{fmtMoney(totalAbs)}</b>) no coincide con el resumen (<b>{fmtMoney(resumenAbs)}</b>). Diferencia: <b>{fmtMoney(Math.abs(diff))}</b>.</>
                  )}
                </div>
              </div>
            )}
            <p className="text-[10px] text-slate-400">
              El importe del Mayor llega con signo contable (egresos negativos). La celda del Resumen muestra el valor convertido a $ Expensas (positivo). Se compara en valor absoluto.
            </p>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default DetalleSubrubroDialog;
